'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { type SearchResults } from 'algoliasearch-helper'
import { useEffect } from 'react'

type ActiveFilter = {
  filter: string
  filterName: string
  hits: number
}

const setDisjunctiveFilter = (
  refinement: SearchResults.Refinement,
  activeFilters: ActiveFilter[],
): void => {
  const { attributeName, count, name } = refinement

  activeFilters.push({
    filter: name,
    filterName: attributeName,
    hits: count,
  })
}

const setRangeFilter = (
  refinement: SearchResults.Refinement,
  activeFilters: ActiveFilter[],
  totalCount: number,
): void => {
  const { attributeName, numericValue, operator } = refinement

  const activeFilter = activeFilters.find(
    (filter) => filter.filterName === attributeName,
  )

  if (activeFilter) {
    const filter = String(numericValue)

    if (operator === '>=') {
      activeFilter.filter = `${filter}${activeFilter.filter}`
    }

    if (operator === '<=') {
      activeFilter.filter = `${activeFilter.filter}${filter}`
    }
  } else {
    let filter = String(numericValue)

    if (operator === '>=') {
      filter = `${filter}-`
    }

    if (operator === '<=') {
      filter = `-${filter}`
    }

    activeFilters.push({
      filter,
      filterName: attributeName,

      /**
       * Very strange logic from Fock to pass total hits amount for range filter
       * https://github.com/RedTecLab/fock/blob/cb77fa3d59c5863f995c56bf60f9a083b9c20c6f/app/shared/utils/gtmUtils.ts#L91.
       * Keep it as it is for backward compatibility.
       */
      hits: totalCount,
    })
  }
}

const getActiveFilters = (
  refinements: SearchResults.Refinement[],
  totalCount: number,
): ActiveFilter[] => {
  const activeFilters: ActiveFilter[] = []

  for (const refinement of refinements) {
    const { type } = refinement

    switch (type) {
      case 'disjunctive': {
        setDisjunctiveFilter(refinement, activeFilters)
        break
      }

      case 'numeric': {
        setRangeFilter(refinement, activeFilters, totalCount)
        break
      }

      default: {
        break
      }
    }
  }

  return activeFilters
}

export const useProductSearchResultsGtmTracker = (
  results: SearchResults | undefined,
): void => {
  useEffect(() => {
    if (!results) {
      return
    }

    const activeFilters = getActiveFilters(
      results.getRefinements(),
      results.nbHits,
    )

    sendGTMEvent({
      algolia: {
        activeFilters,
        pageSize: results.hitsPerPage,
        query: results.query,
        totalCount: results.nbHits,
      },
      filterData: activeFilters,
    })
  }, [results])
}
